import React from 'react';
import { 
  Paper, 
  Typography, 
  Button, 
  Grid,
  Card,
  CardContent,
  Divider,
  Box,
  Tooltip
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import EqualizerIcon from '@mui/icons-material/Equalizer';
import TimelineIcon from '@mui/icons-material/Timeline';
import AssessmentIcon from '@mui/icons-material/Assessment';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import VideocamIcon from '@mui/icons-material/Videocam';
import './ResultsDisplay.css';

const ResultsDisplay = ({ results, inputs, onReset }) => {
  // Check if results is valid and has the expected structure
  if (!results || !results.success) {
    return (
      <div className="results-error">
        <h2>Error Calculating ROI</h2>
        <p>{results?.error || "Unknown error occurred"}</p>
        <button onClick={onReset} className="back-button">Try Again</button>
      </div>
    );
  }

  const { reportId, results: calculationResults } = results;
  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5001';

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const downloadReport = () => {
    window.location.href = `${apiUrl}/api/reports/${reportId}`;
  };

  // Get the C score impact details if available
  const cScoreImpact = calculationResults.cScoreImpact || {};
  const hasCScoreImpact = cScoreImpact && cScoreImpact.cScoreMonthlyRevenue > 0;

  return (
    <Paper className="results-paper">
      <div className="results-header">
        <Typography variant="h4" component="h1" gutterBottom>
          ROI Analysis Results
        </Typography>
        <Typography variant="subtitle1" gutterBottom className="results-subtitle">
          Here's what your investment with IO Health could yield
        </Typography>
      </div>

      <Box className="highlight-box">
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={8}>
            <Typography variant="h5" component="h2" className="highlight-title">
              Your ROI Potential
            </Typography>
            <Typography variant="body1" className="highlight-description">
              Based on the information provided, your organization could see a significant return on investment
              when implementing IO Health solutions.
            </Typography>
          </Grid>
          <Grid item xs={12} md={4} className="highlight-value-container">
            <div className="highlight-value">
              <TrendingUpIcon className="highlight-icon" />
              <Typography variant="h3">{calculationResults.roi.toFixed(1)}x</Typography>
            </div>
            <Typography variant="body2" className="highlight-label">Return on Investment</Typography>
          </Grid>
        </Grid>
      </Box>

      {/* New C Score Impact Section */}
      {hasCScoreImpact && (
        <>
          <Divider className="section-divider" />
          
          <Box className="highlight-box c-score-impact-box" sx={{ background: 'linear-gradient(45deg, #e3f2fd, #bbdefb)' }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={8}>
                <Typography variant="h5" component="h2" className="highlight-title" sx={{ display: 'flex', alignItems: 'center' }}>
                  <AssessmentIcon sx={{ mr: 1 }} />
                  C Functional Score Impact
                  <Tooltip title="IO Health helps improve C functional scores through better documentation accuracy and training">
                    <InfoOutlinedIcon sx={{ ml: 1, fontSize: '0.9rem', color: 'primary.main', cursor: 'pointer' }} />
                  </Tooltip>
                </Typography>
                <Typography variant="body1" className="highlight-description">
                  IO Health can help improve your C functional score percentage from {cScoreImpact.cScoreOriginal}% to a projected {cScoreImpact.cScoreProjected}%,
                  which significantly increases your monthly revenue.
                </Typography>
              </Grid>
              <Grid item xs={12} md={4} className="highlight-value-container">
                <div className="highlight-value">
                  <AttachMoneyIcon className="highlight-icon" />
                  <Typography variant="h3">{formatCurrency(cScoreImpact.cScoreMonthlyRevenue)}</Typography>
                </div>
                <Typography variant="body2" className="highlight-label">Additional Monthly Revenue</Typography>
              </Grid>
            </Grid>
          </Box>
        </>
      )}

      <Divider className="section-divider" />

      <Typography variant="h5" component="h2" className="section-title">
        Financial Impact Summary
      </Typography>

      <Grid container spacing={3} className="cards-container">
        <Grid item xs={12} sm={6} md={4}>
          <Card className="summary-card total-savings-card">
            <CardContent>
              <AttachMoneyIcon className="card-icon" />
              <Typography variant="h5" component="div" className="card-value">
                {formatCurrency(calculationResults.totalSavings)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Annual Savings
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <Card className="summary-card revenue-card">
            <CardContent>
              <EqualizerIcon className="card-icon" />
              <Typography variant="h5" component="div" className="card-value">
                {formatCurrency(calculationResults.pdgmRevenue + calculationResults.qaScrub + calculationResults.growthCapacity)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Revenue Increase
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <Card className="summary-card savings-card">
            <CardContent>
              <PriorityHighIcon className="card-icon" />
              <Typography variant="h5" component="div" className="card-value">
                {formatCurrency(calculationResults.cpvSavings + calculationResults.trainingSavings + 
                  calculationResults.qaSkipSavings + calculationResults.qaReviewSavings)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Operational Savings
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* C Score Improvement Detail Card */}
      {hasCScoreImpact && (
        <Grid container spacing={3} className="cards-container" sx={{ mt: 2 }}>
          <Grid item xs={12}>
            <Card className="detail-card c-score-detail-card" sx={{ background: 'linear-gradient(45deg, #e8f5e9, #c8e6c9)' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>C Functional Score Revenue Impact</Typography>
                
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <div className="detail-stat">
                      <Typography variant="body2" color="text.secondary">Current C Score</Typography>
                      <Typography variant="h6">{cScoreImpact.cScoreOriginal}%</Typography>
                    </div>
                  </Grid>
                  
                  <Grid item xs={12} sm={4}>
                    <div className="detail-stat">
                      <Typography variant="body2" color="text.secondary">Projected C Score</Typography>
                      <Typography variant="h6">{cScoreImpact.cScoreProjected}%</Typography>
                      <Typography variant="caption" sx={{ color: 'success.main' }}>
                        +25 percentage points
                      </Typography>
                    </div>
                  </Grid>
                  
                  <Grid item xs={12} sm={4}>
                    <div className="detail-stat">
                      <Typography variant="body2" color="text.secondary">Monthly Revenue Impact</Typography>
                      <Typography variant="h6">{formatCurrency(cScoreImpact.cScoreMonthlyRevenue)}</Typography>
                      <Typography variant="caption">
                        {cScoreImpact.cScoreProjected >= 60 ? 
                          '$400 per patient/month' : 
                          '$100 per patient/month'}
                      </Typography>
                    </div>
                  </Grid>
                </Grid>
                
                <Typography variant="body2" sx={{ mt: 2 }}>
                  Improving C functional scores directly increases reimbursement rates. With IO Health, you can achieve more accurate 
                  documentation leading to better scoring and substantially increased revenue.
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      <Divider className="section-divider" />

      <Typography variant="h5" component="h2" className="section-title">
        Detailed Breakdown
      </Typography>

      <Grid container spacing={2} className="breakdown-container">
        <Grid item xs={12} sm={6}>
          <div className="breakdown-item">
            <Typography variant="subtitle1">PDGM Revenue Increase</Typography>
            <Typography variant="h6">{formatCurrency(calculationResults.pdgmRevenue)}</Typography>
          </div>
        </Grid>
        <Grid item xs={12} sm={6}>
          <div className="breakdown-item">
            <Typography variant="subtitle1">QA Scrub Opportunity</Typography>
            <Typography variant="h6">{formatCurrency(calculationResults.qaScrub)}</Typography>
          </div>
        </Grid>
        <Grid item xs={12} sm={6}>
          <div className="breakdown-item">
            <Typography variant="subtitle1">Growth Capacity</Typography>
            <Typography variant="h6">{formatCurrency(calculationResults.growthCapacity)}</Typography>
          </div>
        </Grid>
        <Grid item xs={12} sm={6}>
          <div className="breakdown-item">
            <Typography variant="subtitle1">CPV Savings</Typography>
            <Typography variant="h6">{formatCurrency(calculationResults.cpvSavings)}</Typography>
          </div>
        </Grid>
        <Grid item xs={12} sm={6}>
          <div className="breakdown-item">
            <Typography variant="subtitle1">Training Savings</Typography>
            <Typography variant="h6">{formatCurrency(calculationResults.trainingSavings)}</Typography>
          </div>
        </Grid>
        <Grid item xs={12} sm={6}>
          <div className="breakdown-item">
            <Typography variant="subtitle1">QA Skip Savings</Typography>
            <Typography variant="h6">{formatCurrency(calculationResults.qaSkipSavings)}</Typography>
          </div>
        </Grid>
        <Grid item xs={12} sm={6}>
          <div className="breakdown-item">
            <Typography variant="subtitle1">QA Review Savings</Typography>
            <Typography variant="h6">{formatCurrency(calculationResults.qaReviewSavings)}</Typography>
          </div>
        </Grid>
        {hasCScoreImpact && (
          <Grid item xs={12} sm={6}>
            <div className="breakdown-item c-score-item">
              <Typography variant="subtitle1">C Score Revenue (Annual)</Typography>
              <Typography variant="h6">{formatCurrency(cScoreImpact.cScoreMonthlyRevenue * 12)}</Typography>
            </div>
          </Grid>
        )}
        <Grid item xs={12} sm={6}>
          <div className="breakdown-item cost-item">
            <Typography variant="subtitle1">IO Health Cost</Typography>
            <Typography variant="h6">-{formatCurrency(calculationResults.ioHealthCost)}</Typography>
          </div>
        </Grid>
      </Grid>
      
      {/* Revenue Charts Section */}
      {calculationResults.charts && (
        <>
          <Divider className="section-divider" />
          
          <Typography variant="h5" component="h2" className="section-title">
            <TimelineIcon sx={{ verticalAlign: 'middle', marginRight: '8px' }} />
            Revenue Projection (Post-Implementation)
          </Typography>
          
          <Typography variant="body1" className="charts-description">
            These charts show the projected revenue growth over 24 months after implementing IO Health solutions.
            The data reflects only POST-QA results to provide the clearest picture of your potential ROI.
          </Typography>
          
          <Grid container spacing={4} className="charts-container">
            <Grid item xs={12} md={6}>
              <div className="chart-card">
                <Typography variant="h6" className="chart-title">
                  Estimated Revenue per Patient
                </Typography>
                <div className="chart-image-container">
                  {calculationResults.charts?.revenuePerPatient && (
                    <img 
                      src={`${apiUrl}${calculationResults.charts.revenuePerPatient}`} 
                      alt="Estimated Revenue per Patient" 
                      className="chart-image"
                    />
                  )}
                </div>
              </div>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <div className="chart-card">
                <Typography variant="h6" className="chart-title">
                  Estimated Total Monthly Revenue
                </Typography>
                <Typography variant="body2" sx={{ textAlign: 'center', mb: 2 }}>
                  Includes both patient revenue growth and 5% annual practice scaling from IO Health implementation
                </Typography>
                <div className="chart-image-container">
                  {calculationResults.charts?.totalMonthlyRevenue && (
                    <img 
                      src={`${apiUrl}${calculationResults.charts.totalMonthlyRevenue}`} 
                      alt="Estimated Total Monthly Revenue" 
                      className="chart-image"
                    />
                  )}
                </div>
              </div>
            </Grid>
          </Grid>
        </>
      )}
      
      <Divider className="section-divider" />

      <div className="actions-container">
        <Button 
          variant="contained" 
          color="primary" 
          size="large"
          onClick={downloadReport}
          startIcon={<DownloadIcon />}
          className="action-button download-button"
        >
          Download Full Report
        </Button>
        
        <Button 
          variant="outlined" 
          color="primary"
          onClick={onReset}
          startIcon={<RestartAltIcon />}
          className="action-button"
        >
          Start New Calculation
        </Button>
      </div>

      <Divider className="section-divider" style={{ margin: '2rem 0 !important' }} />
      
      <div className="book-call-container">
        <Typography variant="h5" component="h2" className="book-call-title">
          Ready to see how IO Health can transform your organization?
        </Typography>
        <Typography variant="body1" className="book-call-description">
          Schedule a personalized demo with our team to discuss your specific needs and see our solutions in action.
        </Typography>
        <a 
          href={process.env.REACT_APP_BOOKING_LINK || "https://calendly.com/davidb-ghs/30-min-io-demo"} 
          target="_blank"
          rel="noopener noreferrer"
          className="book-call-btn-results"
        >
          <VideocamIcon className="book-call-icon" />
          Book a Call
        </a>
        <Typography variant="caption" className="book-call-note">
          A detailed PDF report has been sent to: {inputs.email}
        </Typography>
      </div>
    </Paper>
  );
};

export default ResultsDisplay;